// What the banner asks about, and every cookie or storage key an answer can
// switch on.
//
// One table, in one file, because a second copy of it goes stale silently. Two
// readers take it as it is: the banner's preferences panel PRINTS it, row for
// row, and a test walks this app's source for every place a cookie or a browser
// storage key is written and fails on a name the table does not know.
//
// The lifetimes are the setters' own numbers rather than prose about them, and
// the test asserts each one against the constant that sets it. A published
// lifetime a browser contradicts is the transparency defect — not the other way
// round — so the code is what the table is read from, in both directions.
//
// The published privacy text is NOT generated from here. It is written and
// versioned as a legal edition, by hand, and it takes these values; keeping the
// two in step is part of publishing an edition, and nothing here claims
// otherwise.
//
// Browser-safe: no Node built-in, no framework import. The server reads it too.

import { COMMENT_SENT_COOKIE, COMMENT_SENT_MAX_AGE_SECONDS } from "../blog/comment-cookie.ts";
import { SIGNED_IN_HINT_COOKIE } from "../nav/signed-in-hint.ts";
import {
  ADMIN_SESSION_COOKIE,
  DELETE_CONFIRM_COOKIE,
  DELETE_CONFIRM_MAX_AGE_SECONDS,
  IMPERSONATING_COOKIE,
  IMPERSONATION_MAX_AGE_SECONDS,
  PENDING_VERIFICATION_COOKIE,
  PENDING_VERIFICATION_MAX_AGE_SECONDS,
  SESSION_COOKIE,
  SESSION_MAX_AGE_SECONDS,
} from "../session-cookies.ts";

/**
 * The categories a visitor can answer. `necessary` is never a question.
 *
 * `experiments` is its own category rather than part of `analytics`, and the
 * reason is the difference between the two sentences a visitor is asked to
 * agree to. Measurement is counting what they did. An experiment SHOWS THEM
 * SOMETHING DIFFERENT — an operator-chosen variant is not a preference they
 * expressed and not a measurement of anything they did, so the
 * user-interface-customisation reasoning does not reach it and it cannot ride
 * under a sentence about counting. Somebody willing to be counted is not
 * thereby willing to be experimented on, and a single switch would take the
 * first answer as the second.
 *
 * Everything that consumes a category — the panel, the cookie table, the
 * server-side check — reads it from here, so adding one is an entry plus its
 * cookies plus a bumped revision.
 */
export const CONSENT_CATEGORIES = ["necessary", "analytics", "experiments"] as const;

export type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];

/** The one category that turns on measurement of any kind, Google Analytics included. */
export const ANALYTICS_CATEGORY: ConsentCategory = "analytics";

/** The one category that lets a visitor be shown a variant of a page. */
export const EXPERIMENTS_CATEGORY: ConsentCategory = "experiments";

/**
 * The cookie the consent library keeps the answer in.
 *
 * It is itself exempt: remembering a preference a visitor expressed is the one
 * storage the published banner report names as essential, and a site that asked
 * again on every page would be storing less and annoying more.
 */
export const CONSENT_COOKIE = "dc_consent";

/**
 * Bumping this invalidates every answer already given and shows the banner
 * again. It moves when the QUESTION changes — a new category, a new purpose
 * under an existing one — and not when the wording is tidied.
 *
 * It moved to 2 with the experiments category: a visitor who answered when
 * there were two questions never answered the third, and carrying their old
 * answer forward would be treating silence as consent.
 *
 * It moved to 3 with Google Analytics: the measurement question gained a
 * recipient outside this site. Somebody who allowed counting by our own
 * servers did not thereby allow counting by Google, so every earlier answer is
 * asked again.
 */
export const CONSENT_REVISION = 3;

/** How long an answer is remembered before the banner asks again. */
export const CONSENT_COOKIE_DAYS = 182;

/** The first-touch cookie, written only under `analytics`. */
export const FIRST_TOUCH_COOKIE = "dc_ft";

/** 180 days, the window the published text states for the analytics cookies. */
export const FIRST_TOUCH_DAYS = 180;

/** The bucketing cookie, written only under `experiments`. */
export const VISITOR_COOKIE = "dc_vid";

/** 180 days, the same window the published text states for `dc_ft`. */
export const VISITOR_DAYS = 180;

/**
 * The two cookies the Google Analytics tag sets on this site's own domain once
 * measurement is allowed: one that tells visitors apart and one per property
 * that keeps the session. Named by the vendor, not by us, and set by its script
 * rather than by anything in this app — which is why the guard over this app's
 * own write sites never meets them.
 */
export const GA_CLIENT_COOKIE = "_ga";
export const GA_SESSION_COOKIE_PREFIX = "_ga_";

/** The vendor's documented default expiry for both: two years. */
export const GA_COOKIE_DAYS = 730;

/** Where the dismissal of a service notice is remembered, one key per notice. */
export const NOTICE_STORAGE_PREFIX = "sd_notice_";

export interface CookieEntry {
  /** The name as it appears in a browser's storage inspector. */
  readonly name: string;
  readonly category: ConsentCategory;
  /** One plain sentence: what it is for, not how it works. */
  readonly purpose: string;
  /**
   * How long it lives, in seconds, or null for storage that goes with the tab.
   *
   * A NUMBER rather than a sentence, so the one test that matters can compare
   * it with the constant that sets it. The sentence a reader sees is derived
   * (`describeLifetime`), which is the only way a published lifetime and the
   * `Set-Cookie` a browser receives cannot drift apart.
   */
  readonly lifetimeSeconds: number | null;
  /** Which process writes it. */
  readonly setBy: string;
  /** `cookie` or a browser-storage kind, because both are in scope. */
  readonly kind: "cookie" | "storage";
  /**
   * True where `name` is the start of a family of keys rather than one key.
   *
   * One entry for a family is the honest shape: the notice keys carry a digest
   * of the message they belong to, so there is no fixed list of them and a
   * table that pretended otherwise would be wrong the day a second notice is
   * published.
   */
  readonly prefix?: true;
}

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * The lifetime as a reader should see it.
 *
 * Derived rather than written down, because a lifetime somebody typed is a
 * lifetime that can disagree with the one a browser is given — which is exactly
 * the discrepancy this table was found to carry.
 */
export function describeLifetime(seconds: number | null): string {
  if (seconds === null) return "Until the browser tab is closed";
  if (seconds < HOUR) return plural(Math.round(seconds / MINUTE), "minute");
  if (seconds < DAY) return plural(Math.round(seconds / HOUR), "hour");
  return plural(Math.round(seconds / DAY), "day");
}

function plural(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? "" : "s"}`;
}

/**
 * Every cookie and storage key this application can set, with the session
 * cookies the cabinet needs named beside the public ones, so the table a reader
 * sees is the whole truth rather than the new half.
 *
 * The measurement tracker is deliberately absent, and its absence is the fact:
 * read at the pinned version it writes no cookie and no browser storage at all
 * — the visitor hash is derived on the server from a salt that rotates daily —
 * and "the tracker sets a cookie" is what every reader assumes. The one thing
 * it touches is a read of `localStorage` for a kill switch it never writes.
 */
export const COOKIE_TABLE: readonly CookieEntry[] = [
  {
    name: CONSENT_COOKIE,
    category: "necessary",
    purpose: "Remembers the choice made in the cookie banner so it is not asked again.",
    lifetimeSeconds: CONSENT_COOKIE_DAYS * DAY,
    setBy: "This site, in the browser",
    kind: "cookie",
  },
  {
    name: SESSION_COOKIE,
    category: "necessary",
    // The one place the number beside a name is not the only number it can
    // have: an administrator viewing another account carries a session that
    // expires with the impersonation rather than in a week. That is an operator
    // path and the shorter window is the safer direction, so the table states
    // the lifetime a visitor is actually given.
    purpose: "Keeps a signed-in visitor signed in.",
    lifetimeSeconds: SESSION_MAX_AGE_SECONDS,
    setBy: "This site, on the server",
    kind: "cookie",
  },
  {
    name: SIGNED_IN_HINT_COOKIE,
    category: "necessary",
    purpose:
      "Marks that a session exists, so a page built ahead of time can offer the cabinet instead of the sign-in link.",
    lifetimeSeconds: SESSION_MAX_AGE_SECONDS,
    setBy: "This site, on the server",
    kind: "cookie",
  },
  {
    name: PENDING_VERIFICATION_COOKIE,
    category: "necessary",
    purpose:
      "Carries the address awaiting confirmation between the form and the confirmation page.",
    lifetimeSeconds: PENDING_VERIFICATION_MAX_AGE_SECONDS,
    setBy: "This site, on the server",
    kind: "cookie",
  },
  {
    name: DELETE_CONFIRM_COOKIE,
    category: "necessary",
    purpose:
      "Remembers that a visitor left the cabinet to sign in again on the way to deleting their account, so the confirmation reopens when they come back.",
    lifetimeSeconds: DELETE_CONFIRM_MAX_AGE_SECONDS,
    setBy: "This site, on the server",
    kind: "cookie",
  },
  {
    name: ADMIN_SESSION_COOKIE,
    category: "necessary",
    purpose:
      "Holds an administrator's own session while they are viewing another account, so stopping is a return rather than a fresh sign-in.",
    lifetimeSeconds: IMPERSONATION_MAX_AGE_SECONDS,
    setBy: "This site, on the server",
    kind: "cookie",
  },
  {
    name: IMPERSONATING_COOKIE,
    category: "necessary",
    purpose:
      "Names the account an administrator is viewing, so the banner saying so is in the page from the first byte.",
    lifetimeSeconds: IMPERSONATION_MAX_AGE_SECONDS,
    setBy: "This site, on the server",
    kind: "cookie",
  },
  {
    name: COMMENT_SENT_COOKIE,
    category: "necessary",
    purpose:
      "Lets a blog comments page say thanks once after a comment was sent; it holds nothing but a marker and is removed when the page reads it.",
    lifetimeSeconds: COMMENT_SENT_MAX_AGE_SECONDS,
    setBy: "This site, on the server",
    kind: "cookie",
  },
  {
    name: NOTICE_STORAGE_PREFIX,
    prefix: true,
    category: "necessary",
    purpose:
      "Remembers that a service notice above the page was dismissed, so it is not shown again for the rest of the visit. One key per notice.",
    lifetimeSeconds: null,
    setBy: "This site, in the browser",
    kind: "storage",
  },
  {
    name: VISITOR_COOKIE,
    category: "experiments",
    purpose:
      "A random label with no name or address in it, so that a visitor who is shown a trial version of a page keeps seeing the same one instead of a different one on every visit.",
    lifetimeSeconds: VISITOR_DAYS * DAY,
    setBy: "This site, on the server",
    kind: "cookie",
  },
  {
    name: FIRST_TOUCH_COOKIE,
    category: "analytics",
    purpose:
      "Records which channel first brought this visitor – the referring site, the campaign parameters and the page landed on – so a registration can be credited to it.",
    lifetimeSeconds: FIRST_TOUCH_DAYS * DAY,
    setBy: "This site, on the server",
    kind: "cookie",
  },
  {
    name: GA_CLIENT_COOKIE,
    category: "analytics",
    purpose:
      "A random number that lets Google Analytics tell one visitor from another when counting visits and how the site is used.",
    lifetimeSeconds: GA_COOKIE_DAYS * DAY,
    setBy: "Google Analytics, in the browser",
    kind: "cookie",
  },
  {
    name: GA_SESSION_COOKIE_PREFIX,
    prefix: true,
    category: "analytics",
    purpose:
      "Keeps the current visit together for Google Analytics, so the pages read in one sitting are counted as one visit.",
    lifetimeSeconds: GA_COOKIE_DAYS * DAY,
    setBy: "Google Analytics, in the browser",
    kind: "cookie",
  },
];

/** The name as a reader should see it — a family is shown as one. */
export function displayName(entry: CookieEntry): string {
  return entry.prefix === true ? `${entry.name}…` : entry.name;
}

/**
 * Where the footer's cookie entry leads when no script is running.
 *
 * The published privacy text is where the cookie table lives, so a visitor
 * without JavaScript reaches the same information by reading it rather than by
 * pressing a control that cannot work. With the consent layer loaded the click
 * is intercepted and opens the preferences panel instead.
 */
export const COOKIE_SETTINGS_HREF = "/privacy";

/** The entries a published cookie table shows for one category. */
export function cookiesInCategory(category: ConsentCategory): readonly CookieEntry[] {
  return COOKIE_TABLE.filter((entry) => entry.category === category);
}
